import { type RefObject, useEffect, useState } from "react";

// Tracks a raw <video> element's playback so a component that owns its own element can drive the
// shared PreviewPlayer control (button + PreviewPlayerProgressRing). It is the video-ref counterpart
// to the audio-provider-bound usePreviewPlayer.
//
// Progress is sampled on a requestAnimationFrame loop while playing rather than from the `timeupdate`
// event: browsers throttle `timeupdate` to a few ticks per second and its last tick lands short of the
// real end, so a ring driven from it visibly lags and completes/restarts out of sync with the clip.
// This mirrors how useDubbedVideoPlayer feeds the same ring. `progressAnimationStartedAt` stays null
// until the first play, telling the ring to track playback rather than sit at its rest dot.
export function useVideoPlaybackState(videoRef: RefObject<HTMLVideoElement | null>) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progressAnimationStartedAt, setProgressAnimationStartedAt] = useState<number | null>(null);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    let frame: number | null = null;

    const sync = () => {
      setCurrentTime(video.currentTime);
      setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    };

    const stopTracking = () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
        frame = null;
      }
    };

    const trackProgress = () => {
      sync();

      if (video.paused) {
        frame = null;

        return;
      }

      frame = window.requestAnimationFrame(trackProgress);
    };

    const startTracking = () => {
      stopTracking();
      frame = window.requestAnimationFrame(trackProgress);
    };

    const markStarted = () => setProgressAnimationStartedAt(previous => previous ?? performance.now());

    const handlePlay = () => {
      setIsPlaying(true);
      markStarted();
      startTracking();
    };
    const handlePause = () => {
      setIsPlaying(false);
      stopTracking();
      sync();
    };
    const handleEnded = () => {
      setIsPlaying(false);
      stopTracking();
      sync();
    };

    // Sync from the element up front: an autoplaying or cached clip may already be playing or have its
    // metadata loaded before this effect attached the listeners.
    sync();
    setIsPlaying(!video.paused);
    if (!video.paused) {
      markStarted();
      startTracking();
    }

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("durationchange", sync);
    video.addEventListener("loadedmetadata", sync);

    return () => {
      stopTracking();
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("durationchange", sync);
      video.removeEventListener("loadedmetadata", sync);
    };
  }, [videoRef]);

  return { currentTime, duration, isPlaying, progressAnimationStartedAt };
}
